import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@progress/kendo-react-buttons';
import MultiSelect from "./MultiSelect";
import Dialog from "../dialogs/Dialog";
import Grid from "../dataGrid/DataGrid";
import MetaGrid from '../meta/MetaGrid';
import flags from "../utils/flags";
import withMeta from '../utils/withMeta';
import { processAfter, processEnumsAfter } from '../utils/helper';
import { useValidation } from '../utils/ValidationProvider';
import "./MultiPicker.scss";

let _filter = { filters: [] }
let _page = { skip: 0, take: 100 }
let _sort = []
let _flag;

/**
 * Multi picker component
 * @module
 * @param {array} data - Data
 * @param {string} textField - Text field
 * @param {array} [selected] - Current selected value
 * @param {func} onChange - Function invoked on filter change
 * @param {func} onSelect - Function invoked on value change
 * @param {bool} [required] - Is field required?
 * @param {string} name - Name
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {bool} [disabled] - Is multiselect readonly?
 * @param {number} [tabIndex] - Multiselect tabindex
 * @param {string} [placeholder] - Placeholder
 * @param {string} title - Picker title
 * @param {bool} isLoading - Loading
 * @param {number} total - Total number of items
 * @param {array} schema - Schema for picker
 */

const MultiPickerComponent = (props) => {
  const filterRef = useRef(_filter)
  const pageRef = useRef(_page)
  const sortRef = useRef(_sort)

  const [isOpen, setIsOpen] = useState(false);
  const [initFilter, setInitFilter] = useState(props.initFilter || { filters: [] });
  const [fixedFilter, setFixedFilter] = useState(props.fixedFilter || { filters: [] });
  const [metaFilters, setMetaFilters] = useState([]);
  const [metaSorts, setMetaSorts] = useState([]);
  const [selectedItems, setSelectedItems] = useState(props.selected);

  const field = useMemo(() => ({
    field: props.name,
    title: props.title,
    type: props.type,
    min: props.min,
    max: props.max,
    minLength: props.minLength,
    maxLength: props.maxLength,
    validationMessage: props.validationMessage,
    pattern: props.pattern,
    required: props.required,
    disabled: props.disabled,
    recommended: props.recommended,
    validationAllowed: props.validationAllowed,
    isInvalid: props.isInvalid
  }), [props.disabled, props.max, props.maxLength, props.min, props.minLength, props.name, props.pattern, props.required, props.title, props.type, props.validationMessage, props.recommended, props.validationAllowed, props.isInvalid]);

  const { validate, setValidationValue } = useValidation(field, props.selected);

  const init = useCallback(async () => {
    if (props.withMeta) {
      const f = await props.meta.getFilters(props.identif || props.primaryField, props.schema, null, props.db);
      const s = await props.meta.getSort(props.identif || props.primaryField, props.schema, props.db);
      setMetaFilters(f);
      setMetaSorts(s);      
    }
    // eslint-disable-next-line
  }, [props.primaryField, JSON.stringify(props.initFilter), JSON.stringify(props.fixedFilter), props.db, props.meta, JSON.stringify(props.schema), JSON.stringify(props.selected), props.textField, props.withMeta])

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    if (props.withMeta) {
      setFixedFilter({ filters: [...(props.fixedFilter ? props.fixedFilter.filters : []), ...metaFilters.filter(x => !x.disabled && !x.CanDisabled).map(x => x.filters).flat()] });
    }
    // eslint-disable-next-line
  }, [props.withMeta, JSON.stringify(props.fixedFilter), metaFilters])

  useEffect(() => {
    if (props.withMeta) {
      setInitFilter(prev => props.initFilter || { filters: [...(prev?.filters ?? []), ...metaFilters.filter(x => !x.disabled && x.CanDisabled).map(x => x.filters).flat()] })
    }
    // eslint-disable-next-line
  }, [props.withMeta, JSON.stringify(props.initFilter), metaFilters])

  const handleOpen = (event) => {
    event.preventDefault();
    setIsOpen(true);
    setSelectedItems(props.selected);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleFilter = (filter) => {
    setInitFilter(filter);
  };

  const handleSelect = (selected, isCopy, selectedAll = []) => {
    setSelectedItems([...selected, ...selectedAll]);
  };

  const handleConfirm = () => {
    setIsOpen(false);
    setInitFilter(_filter);
    props.onSelect(props.name, selectedItems);
    setValidationValue(selectedItems);
    validate();
  };

  const handleChange = (filter, sort, page) => {
    setInitFilter(filter)
    filterRef.current = filter;
    sortRef.current = sort;
    pageRef.current = page;
    props.onChange(filter, sort, page, props.identif);
  };

  const handleChangeGrid = (filter, sort, page) => {
    filterRef.current = filter;
    sortRef.current = sort;
    pageRef.current = page;
    props.onChange(filter, sort, page, props.identif);    
    const selectedWithInputValue = { ...props.selected }; //TODO: value
    setValidationValue(selectedWithInputValue);
  };

  const getFilter = () => {
    const filterObj = initFilter.filters.find((el) => el.field === props.textField);
    return filterObj ? filterObj.value : null;
  };

  const handleReducer = async (action, data, state, frmState) => {
    switch (action) {
      case "SELECT":
        handleSelect(state[state.primaryField]?.selected);
        break;
      case "CREATE":
        setSelectedItems({ ...data.data });
        handleConfirm();
        break;
      default:
        break;
    }
    if (props.reducer) {
      return await props.reducer(action, data, state, frmState);
    } else {
      return state;
    }
  };

  const data = props.data
    .map(x => { return processAfter(props.schema, x) })
    .map(x => { return processEnumsAfter(props.schema, x) });

  return (
    <div className={`form-input multipicker ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")}`} >
      <MultiSelect
        name={props.name}
        data={data}
        onChange={props.onSelect}
        value={props.selected}
        textField={props.textField}
        dataItemKey={props.textField}
        disabled={props.disabled}
        required={props.required}
        validationMessage={props.validationMessage}
        tabIndex={props.tabIndex}
        onBlur={() => { props.onBlur(); validate(); }}
        type={props.type}
        onFilterChange={handleChange}
        placeholder={props.placeholder}
        filter={getFilter()}
        validationAllowed={props.validationAllowed}
        setValidation={(v) => setValidationValue(v)}
      />
      <Button
        type="button"
        onClick={handleOpen}
        icon="more-vertical"
        disabled={props.disabled}
        data-testid="openMultiPickerButton"
      />
      <Dialog
        isOpen={props.externalIsOpen || isOpen}
        title={props.title}
        onClose={handleClose}
        onConfirm={handleConfirm}
        confirmDisabled={!selectedItems}
      >
        {props.detail ?
          <MetaGrid
            primaryField={props.primaryField}
            componentFactory={() => { return null; }}
            akcFactory={props.akcFactory}
            reducer={handleReducer}
            initFilter={initFilter}
            fixedFilter={fixedFilter}
            db={props.db}
          />
          :
          <Grid
            total={props.total}
            data={data}
            isLoading={props.isLoading}
            onChange={handleChangeGrid}
            schema={props.schema}
            multiple={true}
            toolbarHidden={true}
            onFilter={handleFilter}
            onSelect={handleSelect}
            primaryField={props.primaryField}
            onConfirm={handleConfirm}
            identif={props.identif}
            isPicker
            parentProps={props.parentProps}
            selected={props.selected || []}
            currentDataItem={props.currentDataItem}
            hidden={flags.PRINT | flags.RESET | flags.ADD | flags.COPY | flags.DELETE | flags.CANCEL | flags.EDITCELL | flags.USER_FILTER | flags.FULLTEXT_SEARCH}
            withMeta={props.withMeta}
            fixedFilter={fixedFilter}
            db={props.db}
            filter={filterRef.current}
            sort={sortRef.current}
            page={pageRef.current}
            initFilter={initFilter}
            hiddenBack={true}
            onGetList={() => { }}
            onGet={() => { }}
            updateSort={() => { }}
            userFilterExtended={[]}
            name={props.name}
          />
        }
      </Dialog>
    </div>
  );
};

MultiPickerComponent.propTypes = {
  data: PropTypes.array.isRequired,
  textField: PropTypes.string.isRequired,
  selected: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  required: PropTypes.bool,
  name: PropTypes.string.isRequired,
  validationMessage: PropTypes.string,
  disabled: PropTypes.bool,
  tabIndex: PropTypes.number,
  title: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  total: PropTypes.number.isRequired,
  schema: PropTypes.array.isRequired,
};

export default withMeta(MultiPickerComponent);
